import React, { useEffect, useMemo, useState } from "react";
import { Button, Input, Modal, Typography, message } from "antd";
import { MailOutlined } from "@ant-design/icons";
import { useAuth } from "../../../../hooks/useAuth";
import GeneralEmailEditor from "../../../../components/EmailEditor/GeneralEmailEditor";
import { adaptUserEmailConfig } from "../../../../components/EmailEditor/components/EmailEditorForm";
import { sendUniversalEmail } from "../../../../api/flightOperationEmailApi";
import { getPresetSenderAccounts } from "../../../../api/mailApi";
import { EmailType, getInitialOptionValues } from "../../../../config/emailTypeConfig";
import { buildAogMailRequest, buildAogNoticeContent, resolveAogNotice } from "../lib/aogNotice";

const { Text } = Typography;

function sendFailureMessage(error) {
    const data = error?.response?.data;
    const validation = Array.isArray(data?.validation_errors) ? data.validation_errors : [];
    const validationText = validation.map((item) => item?.message).filter(Boolean).join("；");
    if (validationText) return validationText;
    const detail = data?.detail;
    if (Array.isArray(detail)) {
        const detailText = detail.map((item) => item?.msg).filter(Boolean).join("；");
        if (detailText) return detailText;
    }
    if (typeof detail === "string" && detail) return detail;
    if (typeof data?.message === "string" && data.message) return data.message;
    if (typeof error?.message === "string" && error.message) return error.message;
    return "邮件发送失败";
}

/**
 * 选中航段后的「发 AOG 通知」。
 * 值不齐时停在监控页补机号或停场机场。齐了就在本页打开和餐食同一套邮件编辑器，
 * 收件人从「邮箱地址与分组」里选，不跳去新的邮件撰写。
 */
const AogNoticeAction = ({ flights = [], selectedCount = 0 }) => {
    const { user, userEmailConfig, hasPermission } = useAuth();
    const [prompt, setPrompt] = useState(null);
    const [inputValue, setInputValue] = useState("");
    const [fieldError, setFieldError] = useState(null);
    const [mail, setMail] = useState(null);
    const [isSending, setIsSending] = useState(false);
    const [senderOptions, setSenderOptions] = useState([]);
    const [sendingOptions, setSendingOptions] = useState(() => getInitialOptionValues(EmailType.GENERAL));
    const defaultSender = useMemo(() => adaptUserEmailConfig(userEmailConfig), [userEmailConfig]);

    useEffect(() => {
        if (!mail) return undefined;
        let cancelled = false;
        getPresetSenderAccounts()
            .then((res) => {
                if (!cancelled && res?.success) setSenderOptions(Array.isArray(res.data) ? res.data : []);
            })
            .catch((error) => {
                console.error("加载预设发件账号失败:", error);
            });
        return () => {
            cancelled = true;
        };
    }, [mail]);

    const legs = flights.map((flight) => ({
        aircraft_reg: flight?.aircraft_reg,
        arrival_airport: flight?.arrival_airport,
    }));

    const openEditor = (aircraft, airport) => {
        const content = buildAogNoticeContent(aircraft, airport);
        const flightIds = flights.map((flight) => flight?.id).filter((id) => id !== undefined && id !== null);
        setSendingOptions(getInitialOptionValues(EmailType.GENERAL));
        setMail({ ...content, flightIds });
    };

    const applyResult = (result, supply) => {
        if (result.status === "ready") {
            setPrompt(null);
            setFieldError(null);
            openEditor(result.aircraft, result.airport);
            return;
        }
        if (result.status === "multiple_aircraft") {
            setPrompt(null);
            setFieldError(null);
            Modal.warning({ title: "发 AOG 通知", content: result.message });
            return;
        }
        if (result.status !== "needs_aircraft" && result.status !== "needs_airport") return;
        if (prompt && prompt.status === result.status) {
            setFieldError(result.message);
            return;
        }
        setPrompt({ ...result, supply });
        setInputValue("");
        setFieldError(null);
    };

    const start = () => {
        if (legs.length !== selectedCount) {
            Modal.warning({ title: "发 AOG 通知", content: "当前列表里看不到全部选中航段，请重新选择后再发。" });
            return;
        }
        applyResult(resolveAogNotice(legs), {});
    };

    const submitPrompt = () => {
        if (!prompt) return;
        const supply = prompt.status === "needs_aircraft" ? { aircraft: inputValue } : { ...prompt.supply, airport: inputValue };
        applyResult(resolveAogNotice(legs, supply), supply);
    };

    const closePrompt = () => {
        setPrompt(null);
        setInputValue("");
        setFieldError(null);
    };

    const closeEditor = () => {
        if (isSending) return;
        setMail(null);
    };

    const sendMail = async (emailData) => {
        if (!hasPermission("mail:send")) {
            message.error("您没有发送邮件的权限，请联系管理员");
            return;
        }
        const prepared = buildAogMailRequest({
            emailData,
            flightIds: mail?.flightIds || [],
            fallbackHtml: mail?.html || "",
            senderUserId: Number(user?.id),
            sendingOptions: emailData?.sendingOptions || sendingOptions,
        });
        if (!prepared.ok) {
            message.error(prepared.message);
            return;
        }
        setIsSending(true);
        try {
            const response = await sendUniversalEmail(prepared.request);
            if (response?.success) {
                message.success(prepared.request.is_bulk_split ? "AOG 通知已开始逐封发送，可到发件箱查看进度" : response.message || "AOG 通知已发送");
                setMail(null);
                return;
            }
            message.error(response?.message || "邮件发送失败");
        } catch (error) {
            message.error(sendFailureMessage(error));
        } finally {
            setIsSending(false);
        }
    };

    const askingAircraft = prompt?.status === "needs_aircraft";

    return (
        <>
            <Button size="small" icon={<MailOutlined />} onClick={start}>
                发 AOG 通知
            </Button>
            <Modal title="发 AOG 通知" open={Boolean(prompt)} okText="继续" cancelText="取消" onOk={submitPrompt} onCancel={closePrompt} destroyOnHidden>
                <Text>{prompt?.message}</Text>
                <div style={{ marginTop: 12 }}>
                    <Text type="secondary">{askingAircraft ? "机号" : "停场机场"}</Text>
                    <Input
                        autoFocus
                        style={{ marginTop: 8 }}
                        placeholder={askingAircraft ? "例如 9H-WFF" : "例如 HKG"}
                        value={inputValue}
                        onChange={(event) => {
                            setInputValue(event.target.value);
                            setFieldError(null);
                        }}
                        onPressEnter={submitPrompt}
                    />
                    {fieldError ? <Text type="danger">{fieldError}</Text> : null}
                </div>
            </Modal>
            {mail ? (
                <GeneralEmailEditor
                    visible
                    title="发 AOG 通知"
                    emailType={EmailType.GENERAL}
                    sendingOptions={sendingOptions}
                    onSendingOptionsChange={setSendingOptions}
                    senderOptions={senderOptions}
                    defaultSender={defaultSender}
                    subjectTemplate={mail.subject}
                    templateContent={mail.html}
                    onSubmit={sendMail}
                    onCancel={closeEditor}
                    loading={isSending}
                    loadingText="正在发送 AOG 通知..."
                    cancelDisabled={isSending}
                />
            ) : null}
        </>
    );
};

export default AogNoticeAction;
